const DEFAULT_API_BASE_URL =
  process.env.NODE_ENV === "production"
    ? "https://photo-x-yash-bhai.onrender.com/api/v1"
    : "http://localhost:4100/api/v1";
const API_BASE_URL = (
  process.env.REACT_APP_API_BASE_URL || DEFAULT_API_BASE_URL
).replace(/\/$/, "");

export class ApiError extends Error {
  constructor(message, status, code, details) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

async function request(path, { method = "GET", body, signal } = {}) {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method,
    signal,
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...(body ? { "Content-Type": "application/json" } : {}),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok || payload?.success === false) {
    throw new ApiError(
      payload?.error?.message ||
        `Request failed with status ${response.status}`,
      response.status,
      payload?.error?.code,
      payload?.error?.details,
    );
  }
  return payload;
}

export async function requestOtp(email) {
  const payload = await request("/auth/otp/request", {
    method: "POST",
    body: {
      email,
      audience: "user",
      ...(globalThis.location?.href
        ? { returnUrl: globalThis.location.href }
        : {}),
    },
  });
  return payload.data;
}

export async function verifyOtp(email, code, intent) {
  const payload = await request("/auth/otp/verify", {
    method: "POST",
    body: { email, code, audience: "user", ...(intent ? { intent } : {}) },
  });
  return payload.data;
}

export async function signOut() {
  return request("/auth/logout", { method: "POST" });
}

export async function loadInquiryEventTypes(signal) {
  const payload = await request("/inquiries/event-types", { signal });
  return payload.data.eventTypes;
}

export async function loadPublicPlans(signal) {
  const payload = await request("/billing/plans", { signal });
  return payload.data.plans;
}

export async function submitEventInquiry(values) {
  const payload = await request("/inquiries", { method: "POST", body: values });
  return payload.data.inquiry;
}

export async function startInquiryPlanCheckout(inquiryId) {
  const payload = await request(
    `/client/inquiries/${encodeURIComponent(inquiryId)}/plan-checkout`,
    { method: "POST" },
  );
  return payload.data.checkout;
}

export async function reconcileInquiryPlanCheckout(inquiryId, sessionId) {
  const payload = await request(
    `/inquiries/${encodeURIComponent(inquiryId)}/plan-checkout/reconcile`,
    { method: "POST", body: { sessionId } },
  );
  return payload.data;
}

export async function resolveEventAccess(token, signal) {
  const payload = await request("/event-access/resolve", {
    method: "POST",
    body: { token },
    signal,
  });
  return payload.data;
}

export async function joinEvent(token) {
  const payload = await request("/event-access/join", {
    method: "POST",
    body: { token },
  });
  return payload.data;
}

export async function loadWorkspace(signal) {
  const [account, events, memberships] = await Promise.all([
    request("/account/me", { signal }),
    request("/events?limit=100&sort=newest", { signal }),
    request("/account/memberships", { signal }),
  ]);
  return {
    user: account.data.user,
    events: events.data,
    memberships: memberships.data,
  };
}

// The API paginates event galleries. Limit signed-URL requests so thousands of
// photos never create an unbounded burst of requests.
export async function loadGalleryPage(
  eventId,
  scope = "visible",
  page = 1,
  signal,
  limit = 24,
) {
  const safePage = Number.isInteger(page) && page > 0 ? page : 1;
  const size = Math.max(1, Math.min(Number(limit) || 24, 50));
  const gallery = await request(
    `/events/${encodeURIComponent(eventId)}/gallery?scope=${encodeURIComponent(scope)}&page=${safePage}&limit=${size}`,
    { signal },
  );
  const photos = Array.isArray(gallery.data) ? gallery.data : [];
  const result = photos.map((photo) => ({ ...photo, mediaUrl: null }));
  let cursor = 0;
  await Promise.all(
    Array.from({ length: Math.min(4, result.length) }, async () => {
      while (cursor < result.length) {
        if (signal?.aborted) return;
        const photo = result[cursor++];
        if (
          !photo.preview?.available &&
          !photo.thumbnail?.available &&
          !photo.original?.available
        )
          continue;
        const variant = photo.thumbnail?.available
          ? "thumbnail"
          : photo.preview?.available
            ? "preview"
            : "original";
        try {
          const media = await request(
            `/events/${encodeURIComponent(eventId)}/photos/${encodeURIComponent(photo.id)}/media?variant=${variant}`,
            { signal },
          );
          photo.mediaUrl = media.data?.delivery?.url || null;
        } catch (error) {
          if (error.name === "AbortError") throw error;
        }
      }
    }),
  );
  const pagination = gallery.meta || {};
  return {
    photos: result,
    pagination: {
      page: safePage,
      limit: size,
      total: pagination.total ?? result.length,
      pages:
        pagination.pages ?? (result.length < size ? safePage : safePage + 1),
    },
  };
}

// Backward-compatible API for callers that expect a simple array.
export async function loadGallery(eventId, scope = "visible", signal) {
  return (await loadGalleryPage(eventId, scope, 1, signal)).photos;
}

export async function setFavorite(eventId, photoId, favorite) {
  return request(`/events/${eventId}/favorites/${photoId}`, {
    method: favorite ? "PUT" : "DELETE",
  });
}

export async function setSharing(eventId, photoId, shared) {
  return request(
    `/events/${eventId}/photos/${photoId}/${shared ? "share" : "unshare"}`,
    { method: "POST" },
  );
}

export async function loadGuestUploadPolicy(eventId, signal) {
  const payload = await request(`/events/${eventId}/upload-policy`, { signal });
  return payload.data.policy;
}

export function validateGuestFile(file, policy) {
  if (!policy?.supportedMimeTypes?.includes(file.type)) {
    throw new ApiError(
      "Choose a JPG, PNG, HEIC or HEIF photo.",
      422,
      "UNSUPPORTED_MEDIA_FORMAT",
    );
  }
  if (file.size < 1 || file.size > policy.maxFileSizeBytes) {
    throw new ApiError(
      `Each photo must be smaller than ${Math.round(policy.maxFileSizeBytes / 1048576)} MiB.`,
      413,
      "MEDIA_FILE_TOO_LARGE",
    );
  }
}

async function sha256(file) {
  if (!globalThis.crypto?.subtle) return undefined;
  const digest = await globalThis.crypto.subtle.digest(
    "SHA-256",
    await file.arrayBuffer(),
  );
  let binary = "";
  for (const byte of new Uint8Array(digest))
    binary += String.fromCharCode(byte);
  return globalThis.btoa(binary);
}

export function uploadBlob(
  url,
  { method = "PUT", headers = {}, body, signal, onProgress },
) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, url);
    Object.entries(headers).forEach(([name, value]) =>
      xhr.setRequestHeader(name, value),
    );
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress?.(event.loaded, event.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve(xhr);
      else
        reject(
          new ApiError(
            "Object storage upload failed",
            xhr.status,
            "STORAGE_UPLOAD_FAILED",
          ),
        );
    };
    xhr.onerror = () =>
      reject(
        new ApiError(
          "Network interrupted the upload",
          0,
          "UPLOAD_NETWORK_ERROR",
        ),
      );
    xhr.onabort = () =>
      reject(new DOMException("Upload cancelled", "AbortError"));
    const abort = () => xhr.abort();
    signal?.addEventListener("abort", abort, { once: true });
    xhr.onloadend = () => signal?.removeEventListener("abort", abort);
    xhr.send(body);
  });
}

export async function createEvent(values) {
  const payload = await request("/events", { method: "POST", body: values });
  return payload.data.event;
}

export async function rotateEventAccess(eventId) {
  const payload = await request(`/events/${eventId}/access/rotate`, {
    method: "POST",
    body: {},
  });
  return payload.data.access;
}

export async function uploadPhoto(
  eventId,
  file,
  kind,
  {
    clientUploadId = globalThis.crypto?.randomUUID?.(),
    signal,
    onProgress,
  } = {},
) {
  const checksumSha256 = await sha256(file);
  const initiated = await request(`/events/${eventId}/uploads`, {
    method: "POST",
    signal,
    body: {
      kind,
      originalFilename: file.name,
      mimeType: file.type,
      sizeBytes: file.size,
      checksumSha256,
      clientUploadId,
      mode: "auto",
    },
  });
  const upload = initiated.data.upload;
  if (upload.mode === "complete") return initiated.data.photo;
  try {
    let parts;
    if (upload.mode === "multipart") {
      parts = [];
      let uploadedBytes = 0;
      for (
        let partNumber = 1;
        partNumber <= upload.partCount;
        partNumber += 1
      ) {
        const start = (partNumber - 1) * upload.partSizeBytes;
        const blob = file.slice(
          start,
          Math.min(file.size, start + upload.partSizeBytes),
        );
        const signed = await request(
          `/events/${eventId}/uploads/${initiated.data.photo.id}/parts`,
          {
            method: "POST",
            body: { partNumber },
            signal,
          },
        );
        const xhr = await uploadBlob(signed.data.part.url, {
          method: signed.data.part.method,
          headers: signed.data.part.headers,
          body: blob,
          signal,
          onProgress: (loaded) =>
            onProgress?.(uploadedBytes + loaded, file.size),
        });
        uploadedBytes += blob.size;
        parts.push({
          partNumber,
          etag: (() => {
            const etag = xhr.getResponseHeader("ETag");
            if (!etag)
              throw new ApiError(
                "Storage upload is missing the ETag header. Configure Cloudflare R2 CORS ExposeHeaders with ETag.",
                0,
                "MULTIPART_ETAG_MISSING",
              );
            return etag;
          })(),
        });
      }
    } else {
      await uploadBlob(upload.url, {
        method: upload.method,
        headers: upload.headers,
        body: file,
        signal,
        onProgress,
      });
    }
    await request(
      `/events/${eventId}/uploads/${initiated.data.photo.id}/complete`,
      {
        method: "POST",
        body: parts ? { parts } : {},
        signal,
      },
    );
    return initiated.data.photo;
  } catch (error) {
    // Once metadata has been created, every failed transfer must release the
    // server-side upload reservation. Otherwise a browser CORS/network failure
    // leaves an empty `uploading` photo that consumes the Guest's five slots.
    await request(`/events/${eventId}/uploads/${initiated.data.photo.id}`, {
      method: "DELETE",
    }).catch(() => undefined);
    throw error;
  }
}

export async function retryPhotoProcessing(eventId, photoId) {
  const payload = await request(
    `/events/${eventId}/uploads/${photoId}/retry-processing`,
    {
      method: "POST",
    },
  );
  return payload.data.photo;
}

export async function abortPhotoUpload(eventId, photoId) {
  const payload = await request(`/events/${eventId}/uploads/${photoId}`, {
    method: "DELETE",
  });
  return payload.data.photo;
}

export async function faceSearch(eventId, selfieBase64, consentConfirmed) {
  if (consentConfirmed !== true)
    throw new ApiError(
      "You must explicitly consent to event-scoped face search.",
      422,
      "FACE_CONSENT_REQUIRED",
    );
  const payload = await request(`/events/${eventId}/face-search`, {
    method: "POST",
    body: { selfieBase64, consentConfirmed: true },
  });
  return payload.data.matches;
}

export async function loadFaceSearchReadiness(eventId, signal) {
  const payload = await request(
    `/events/${encodeURIComponent(eventId)}/face-search/readiness`,
    { signal },
  );
  return payload.data;
}

/** Fetch temporary preview URLs for matched photos even when they are beyond the
 * initially loaded gallery page. Each delivery is authorized by the backend. */
export async function hydrateFaceMatches(eventId, matches, galleryPhotos = []) {
  const fromGallery = new Map(
    galleryPhotos.map((photo) => [photo.id, photo.mediaUrl]),
  );
  const enriched = Array.from(matches || []).map((match) => ({
    ...match,
    mediaUrl: fromGallery.get(match.photo.id) || null,
  }));
  let cursor = 0;
  await Promise.all(
    Array.from({ length: Math.min(4, enriched.length) }, async () => {
      while (cursor < enriched.length) {
        const item = enriched[cursor++];
        if (item.mediaUrl || item.photo.status !== "ready") continue;
        try {
          const response = await request(
            `/events/${eventId}/photos/${item.photo.id}/media?variant=${item.photo.preview?.available ? "preview" : "original"}`,
          );
          item.mediaUrl = response.data.delivery.url;
        } catch {
          // Photos may have been unshared/revoked between the search and delivery.
          item.mediaUrl = null;
        }
      }
    }),
  );
  return enriched;
}

export { API_BASE_URL };
